import { yearEarlierChange } from '@servwell/shared';

/**
 * A figure's change against the same days a year earlier, as every screen prints it: the one
 * comparison the product makes (ops insights Phase 2, spec Q1). The rule itself is the shared
 * `yearEarlierChange`; this is only how it reads.
 *
 * The direction is in the SIGN ("+28.6%", "−100.0%"), and in a word for a screen reader ("up",
 * "down"), never in colour alone. A change with nothing to compare with (a year-earlier figure
 * of zero or less, or none) reads "—" with "nothing a year earlier", never "+100%" or "∞", so a
 * new shop does not look like it doubled.
 */

/** A change in percent, to one decimal, signed: "+28.6%", "−100.0%", "0.0%"; "—" for none. */
export function changeText(change: number | null): string {
  if (change === null) return '—';
  const size = `${Math.abs(change).toFixed(1)}%`;
  // U+2212, the minus sign, not a hyphen: the same width as the plus, and read as "minus".
  return change > 0 ? `+${size}` : change < 0 ? `−${size}` : size;
}

/**
 * `now` against `yearEarlier`, read once for every way it is shown: the change (null for "—"),
 * its direction in a word, and, when there is none, why: "nothing a year earlier" (a
 * year-earlier figure of zero or less, spec "Periods"), or, when `now` is null (an average over
 * no jobs, the one compared figure that can be none), "no jobs in the period".
 */
function compared(now: number | null, yearEarlier: number | null) {
  const change =
    now === null || yearEarlier === null ? null : yearEarlierChange(now, yearEarlier);
  const reason =
    now === null
      ? 'no jobs in the period'
      : change === null
        ? 'nothing a year earlier'
        : null;
  const direction = change === null || change === 0 ? null : change > 0 ? 'up' : 'down';
  return { change, reason, direction };
}

/**
 * The same change as a sentence, for an accessible name that says a figure whole (the Revenue
 * chart's months): "Up 50.0%", "Down 100.0%", "No change", or the reason there is none,
 * "Nothing a year earlier". The words `Change` draws, without the signs and the dash a screen
 * reader would read out.
 */
export function changeWords(now: number | null, yearEarlier: number | null): string {
  const { change, reason, direction } = compared(now, yearEarlier);
  if (change === null) return `${reason![0]!.toUpperCase()}${reason!.slice(1)}`;
  if (direction === null) return 'No change';
  return `${direction === 'up' ? 'Up' : 'Down'} ${Math.abs(change).toFixed(1)}%`;
}

/**
 * `now` against `yearEarlier`: the change, then what it is against ("vs Sep 1–28, 2025"). With
 * nothing to compare, "—" and why (`compared`). Without `against` (a table column that names it
 * in its header, a tooltip that names the days beside it), the change alone.
 */
export function Change({
  now,
  yearEarlier,
  against,
}: {
  now: number | null;
  yearEarlier: number | null;
  against?: string;
}) {
  const { change, reason, direction } = compared(now, yearEarlier);
  const note = reason ?? (against ? `vs ${against}` : null);
  return (
    <>
      {direction ? <span className="sr-only">{direction} </span> : null}
      <span className="font-semibold tabular-nums text-foreground">
        {changeText(change)}
      </span>
      {note ? (
        <>
          {' '}
          <span className="text-muted-foreground">{note}</span>
        </>
      ) : null}
    </>
  );
}
